/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Derived status for the schedule's latest sync state.
 */
export const DeviceIntegrationScheduleStateStatus = {
  Pending: "pending",
  Success: "success",
  Failed: "failed",
  AutoPaused: "auto_paused",
  Disabled: "disabled",
} as const;
/**
 * Derived status for the schedule's latest sync state.
 */
export type DeviceIntegrationScheduleStateStatus = OpenEnum<
  typeof DeviceIntegrationScheduleStateStatus
>;

/**
 * State of one sync schedule of a provider integration: the user's intent (enabled) plus the scheduler's execution state.
 */
export type DeviceIntegrationScheduleState = {
  /**
   * ISO 8601 timestamp when the scheduler auto-paused the schedule after repeated provider rejections. Omitted unless auto-paused.
   */
  autoPausedAt?: Date | undefined;
  /**
   * Number of consecutive failed syncs. Resets to zero on success or retry.
   */
  consecutiveFailures: number;
  /**
   * Whether the user has this schedule enabled. Disabled schedules are skipped by the scheduler until re-enabled.
   */
  enabled: boolean;
  /**
   * Stored error from the schedule's latest failed sync. Omitted unless the latest sync failed.
   */
  lastSyncError?: string | undefined;
  /**
   * ISO 8601 timestamp for the schedule's latest failed sync. Omitted unless a sync has failed.
   */
  lastSyncFailedAt?: Date | undefined;
  /**
   * ISO 8601 timestamp for the schedule's last successful sync. Omitted until a sync succeeds.
   */
  lastSyncSuccessAt?: Date | undefined;
  /**
   * ISO 8601 timestamp for the schedule's next scheduled sync.
   */
  nextSyncAfter?: Date | undefined;
  /**
   * Schedule identifier (e.g. jamf_inventory).
   */
  schedule: string;
  /**
   * Derived status for the schedule's latest sync state.
   */
  status: DeviceIntegrationScheduleStateStatus;
};

/** @internal */
export const DeviceIntegrationScheduleStateStatus$inboundSchema: z.ZodMiniType<
  DeviceIntegrationScheduleStateStatus,
  unknown
> = openEnums.inboundSchema(DeviceIntegrationScheduleStateStatus);

/** @internal */
export const DeviceIntegrationScheduleState$inboundSchema: z.ZodMiniType<
  DeviceIntegrationScheduleState,
  unknown
> = z.pipe(
  z.object({
    auto_paused_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    consecutive_failures: z.int(),
    enabled: z.boolean(),
    last_sync_error: z.optional(z.string()),
    last_sync_failed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_sync_success_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    next_sync_after: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    schedule: z.string(),
    status: DeviceIntegrationScheduleStateStatus$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "auto_paused_at": "autoPausedAt",
      "consecutive_failures": "consecutiveFailures",
      "last_sync_error": "lastSyncError",
      "last_sync_failed_at": "lastSyncFailedAt",
      "last_sync_success_at": "lastSyncSuccessAt",
      "next_sync_after": "nextSyncAfter",
    });
  }),
);

export function deviceIntegrationScheduleStateFromJSON(
  jsonString: string,
): SafeParseResult<DeviceIntegrationScheduleState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DeviceIntegrationScheduleState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DeviceIntegrationScheduleState' from JSON`,
  );
}
